import { memo, useCallback, useMemo } from 'react';

import { StructuredAnswer, StructuredSurveyTemplate } from '@dreryk/egabinet-survey-frontend-api';

import { StrongText, StyledFlex } from '@dreryk/react-components';

import { useMedicalProfileFormContext } from '../MedicalProfileFormContext';
import { SectionNodeViewModel, SectionRowViewModel } from '../tab/MedicalProfileTab.types';
import { collectRowControlQuestionIds } from '../tab/MedicalProfileTab.utils';

import { EntryList } from './fields/EntryList';
import { GateRadioGroupField } from './fields/GateRadioGroupField';
import { ShowOnVisitToggleField } from './fields/ShowOnVisitToggleField';
import { SectionNodeFields } from './SectionNodeFields';
import { useSectionScroll, useVisibilityResolver } from './SectionRow.hooks';
import { StyledSectionRow } from './SectionRow.styled';
import { SubtitleSlot } from './SubtitleSlot';

type SectionRowProps = {
   readonly templateCode: string;
   readonly template: StructuredSurveyTemplate;
   readonly initialAnswers: StructuredAnswer[];
   readonly row: SectionRowViewModel;
   readonly expandedSectionId?: number;
};

export const SectionRow = memo(
   (props: SectionRowProps): JSX.Element => {
      const { register, readonly } = useMedicalProfileFormContext();
      const renderEntryList = useCallback(
         (node: SectionNodeViewModel): JSX.Element => (
            <EntryList
               templateCode={props.templateCode}
               template={props.template}
               initialAnswers={props.initialAnswers}
               node={node}
            />
         ),
         [props.templateCode, props.template, props.initialAnswers]
      );
      const isDeepLinkTarget = props.expandedSectionId === props.row.sectionId;
      const scrollRef = useSectionScroll(isDeepLinkTarget);
      const controlQuestionIds = useMemo(() => collectRowControlQuestionIds(props.template, props.row), [
         props.template,
         props.row,
      ]);
      const visibility = useVisibilityResolver({
         templateCode: props.templateCode,
         template: props.template,
         initialAnswers: props.initialAnswers,
         controlQuestionIds: controlQuestionIds,
         assumeYesQuestionId: isDeepLinkTarget ? props.row.gateQuestionId : undefined,
      });

      return (
         <StyledSectionRow ref={scrollRef} direction={'column'} gap={16}>
            <StyledFlex justifyContent={'space-between'} alignItems={'center'} gap={16}>
               <StyledFlex direction={'column'}>
                  <StrongText testId={`sectionTitle-${props.row.sectionId}`}>{props.row.title}</StrongText>
                  <SubtitleSlot hasData={props.row.hasData} lastUpdateDate={props.row.lastUpdateDate} />
               </StyledFlex>
               <StyledFlex alignItems={'center'} gap={16}>
                  <GateRadioGroupField
                     register={register}
                     templateCode={props.templateCode}
                     row={props.row}
                     disabled={readonly}
                  />
                  <ShowOnVisitToggleField
                     register={register}
                     templateCode={props.templateCode}
                     row={props.row}
                     disabled={readonly}
                  />
               </StyledFlex>
            </StyledFlex>
            <SectionNodeFields
               register={register}
               templateCode={props.templateCode}
               nodes={props.row.details}
               visibility={visibility}
               disabled={readonly}
               renderRepeatable={renderEntryList}
            />
         </StyledSectionRow>
      );
   }
);
